import React, {useState} from 'react';
import {Button, CircularProgress, Grid, TextField} from '@material-ui/core';
import {ISupplierUserProfile} from 'constants/interfaces';

export type CalculatePriceFormSubmission = {
  costPrice: number;
  sellingPrice: number;
  marginPercentage: number;
};

export function CalculatePriceForm(props: {
  user: ISupplierUserProfile;
  isLoading?: boolean;
  submitButtonTitle?: string;
  onSubmit: (submission: CalculatePriceFormSubmission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
}) {
  const [costPrice, setCostPrice] = useState<number>(0);
  const [costPriceError, setCostPriceError] = useState<string>();
  const [marginPercentage, setMarginPercentage] = useState<number>(
    props.user.mainSupplier?.marginPercentage || 0,
  );
  const [marginPercentageError, setMarginPercentageError] = useState<string>();
  const [sellingPrice, setSellingPrice] = useState<number>(0);
  const [sellingPriceError, setSellingPriceError] = useState<string>();

  const handleCostPriceChange = (value: string) => {
    if (isNaN(value as any)) {
      setCostPriceError('Cost Price should be a valid number');
      return;
    }

    const costPrice = parseFloat(value);
    setCostPrice(costPrice);
    setCostPriceError(undefined);

    if (marginPercentage) {
      const sellingPrice = costPrice * (1 + marginPercentage / 100.0);
      setSellingPrice(parseFloat(sellingPrice.toFixed(2)));
    }
  };

  const handleMarginPercentageChange = (value: string) => {
    if (isNaN(value as any)) {
      setMarginPercentageError('Margin should be a valid number');
      return;
    }

    const marginPercentage = parseFloat(value);
    setMarginPercentage(marginPercentage);
    setMarginPercentageError(undefined);

    if (costPrice) {
      const sellingPrice = costPrice * (1 + marginPercentage / 100.0);
      setSellingPrice(parseFloat(sellingPrice.toFixed(2)));
    }
  };

  const handleSellingPriceChange = (value: string) => {
    if (isNaN(value as any)) {
      setSellingPriceError('Selling price should be a valid number');
      return;
    }

    const sellingPrice = parseFloat(value);
    setSellingPrice(sellingPrice);
    setSellingPriceError(undefined);

    if (marginPercentage != undefined) {
      const costPrice = sellingPrice / (1 + marginPercentage / 100.0);
      setCostPrice(parseFloat(costPrice.toFixed(2)));
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    props.onSubmit({
      costPrice: costPrice!,
      sellingPrice: sellingPrice!,
      marginPercentage: marginPercentage!,
    });
  };

  const canSubmit =
    !!costPrice &&
    !costPriceError &&
    !!marginPercentage &&
    !marginPercentageError &&
    !!sellingPrice &&
    !sellingPriceError;

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <TextField
                className="input"
                label="Cost Price (R)"
                variant="outlined"
                fullWidth
                type="number"
                InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                value={costPrice}
                onChange={(event) => handleCostPriceChange(event.target.value)}
                error={!!costPriceError}
                helperText={costPriceError}
                disabled={props.isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                className="input"
                label="Margin (%)"
                variant="outlined"
                fullWidth
                type="number"
                InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                value={marginPercentage}
                onChange={(event) =>
                  handleMarginPercentageChange(event.target.value)
                }
                error={!!marginPercentageError}
                helperText={marginPercentageError}
                disabled={
                  props.isLoading || !!props.user.mainSupplier?.marginPercentage
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                className="input"
                label="Selling Price (R)"
                variant="outlined"
                fullWidth
                type="number"
                InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                value={sellingPrice}
                onChange={(event) =>
                  handleSellingPriceChange(event.target.value)
                }
                error={!!sellingPriceError}
                helperText={sellingPriceError}
                disabled={props.isLoading}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            style={{paddingTop: '1rem', paddingBottom: '1rem'}}
          >
            <Grid item xs={12} sm={6}>
              {props.cancelButtonTitle && (
                <Button
                  className={'secondary-btn'}
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={props.isLoading}
                  onClick={props.onCancel}
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                className={'primary-btn'}
                variant="contained"
                disableElevation
                fullWidth
                color="primary"
                disabled={props.isLoading || !canSubmit}
                onClick={handleSubmit}
              >
                {props.isLoading ? (
                  <CircularProgress color={'primary'} size={24} thickness={6} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
